import React, {
  useState,
  createContext,
  useEffect,
  PropsWithChildren,
} from "react";

import noop from "@brexhq/next/src/utils/noop";
import updateCookie from "@brexhq/next/src/utils/updateCookies";
import { IBynderImageWithBorderRadius } from "@brexhq/shared/types/BynderAsset";
import { fetchPartnerDataFromSanityInBrowser } from "@brexhq/shared/utils/sanity/config";

import {
  mockLogNavigate,
  FrameWorkAgnosticLink,
} from "../components/shared/Link/MockLinkFns";
import useAnalytics from "../hooks/useAnalytics";

export type PartnerData = {
  readonly _type: "partnerPageMeta";
  description: string;
  introDescription: string;
  pageName: string;
  pageToRedirectTo: {
    _ref: string;
    _type: "reference";
  };
  partnerLogo: IBynderImageWithBorderRadius;
  redirectToHome: boolean;
  ref_code: string;
  slug: {
    _type: "slug";
    current: "string";
  };
  title: string;
};

export type PartnerQueryData = {
  meta: PartnerData;
};

export interface PartnerState {
  isVisible: boolean;
  hasPartnerId: boolean;
  response?: PartnerData;
}
interface PartnerStateWithHandler extends PartnerState {
  updatePartnerBanner: (partnerState: Partial<PartnerState>) => void;
}

export interface SiteContextState {
  navigators: {
    component: React.ElementType;
    function: (to: string) => void;
  };
  partnerBanner: PartnerStateWithHandler;
}

export const initialState: SiteContextState = {
  navigators: {
    component: FrameWorkAgnosticLink,
    function: mockLogNavigate,
  },
  partnerBanner: {
    hasPartnerId: false,
    isVisible: false,
    response: {} as PartnerData,
    updatePartnerBanner: noop,
  },
};

/**
 *
 * @param partnerId - partner id from sanity
 * @param isPreview - from query params, tells us which sanity client to use
 * @returns data to show in partner banner. In preview mode we fetch the draft document as well and then just return the one that was most recently updated to show our users in preview
 */
const getPartnerData = (
  partnerId: string,
  isPreview?: boolean,
): Promise<PartnerQueryData> => {
  const result = fetchPartnerDataFromSanityInBrowser({
    isPreview: !!isPreview,
    partnerId,
  });
  return result;
};

export const SiteContext = createContext<SiteContextState>(initialState);

export const SiteProvider: React.FC<
  PropsWithChildren<{
    navigators: SiteContextState["navigators"];
  }>
> = ({ children, navigators }) => {
  const [partnerBanner, setPartnerBanner] = useState<PartnerState>({
    ...initialState.partnerBanner,
  });

  const { identify } = useAnalytics();

  const updatePartnerBanner = React.useCallback(
    (valueToUpdate: Partial<PartnerState>) => {
      setPartnerBanner((prevState: PartnerState) => {
        return {
          ...prevState,
          ...valueToUpdate,
        };
      });
    },
    [],
  );

  useEffect(() => {
    // attempt to fetch partner bar
    const parsedParams = new URLSearchParams(location.search);
    if (parsedParams.has("partnerId")) {
      // Do this first so layout knows to not show any other banners while we load data for the partner banner
      updatePartnerBanner({ hasPartnerId: true });

      getPartnerData(
        parsedParams.get("partnerId") as string,
        // For partner pages only we check via a query param as this query doesn't happen in next data fetching methods instead client-side
        parsedParams.has("isPartnerPreview"),
      )
        .then((result) => {
          updatePartnerBanner({
            isVisible: true,
            response: result.meta,
          });
          updateCookie("ref_code", result.meta.ref_code);
          identify({ properties: { ref_code: result.meta.ref_code } });
        })
        .catch((e: any) => {
          console.error("Error fetching partner data:", e);
        });
    }
    if (parsedParams.has("pref_token")) {
      updateCookie("pref_token", parsedParams.get("pref_token") as string);
    }
    // Disabling because we want this to run once and only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SiteContext.Provider
      value={{
        navigators,
        partnerBanner: {
          ...partnerBanner,
          updatePartnerBanner,
        },
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};
